import React, { useCallback, useEffect, useRef } from "react";
import {
    EmblaCarouselType,
    EmblaEventType,
    EmblaOptionsType
} from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import { useDotButton } from "./Component-EmblaCarouselDotButton";
import styled from "styled-components";
import { size, theme } from "@/config/theme";
import { breakpoints } from "@/utils/breakpoint";
import Autoplay from "embla-carousel-autoplay";
import PlanAndPriceItemForId from "./Component-PlanAndPriceItemForId";

const TWEEN_FACTOR_BASE = 0.84;

const numberWithinRange = (number: number, min: number, max: number): number =>
    Math.min(Math.max(number, min), max);

interface SellingPoint {
    plan: string;
    name: string;
    price1: { region: string; amount: string };
    price2: { region: string; amount: string };
    detailList: string[];
    popular: boolean;
}

type PropType = {
    // slides: number[];
    slides: SellingPoint[];
    options?: EmblaOptionsType;
};

const EmblaCarousel: React.FC<PropType> = (props) => {
    const { slides, options } = props;
    const [emblaRef, emblaApi] = useEmblaCarousel(options, [Autoplay()]);
    const tweenFactor = useRef(0);

    const { selectedIndex, scrollSnaps } = useDotButton(emblaApi);

    const setTweenFactor = useCallback((emblaApi: EmblaCarouselType) => {
        tweenFactor.current =
            TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length;
    }, []);

    const tweenOpacity = useCallback(
        (emblaApi: EmblaCarouselType, eventName?: EmblaEventType) => {
            const engine = emblaApi.internalEngine();
            const scrollProgress = emblaApi.scrollProgress();
            const slidesInView = emblaApi.slidesInView();
            const isScrollEvent = eventName === "scroll";

            emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
                let diffToTarget = scrollSnap - scrollProgress;
                const slidesInSnap = engine.slideRegistry[snapIndex];

                slidesInSnap.forEach((slideIndex) => {
                    if (isScrollEvent && !slidesInView.includes(slideIndex))
                        return;

                    if (engine.options.loop) {
                        engine.slideLooper.loopPoints.forEach((loopItem) => {
                            const target = loopItem.target();

                            if (slideIndex === loopItem.index && target !== 0) {
                                const sign = Math.sign(target);

                                if (sign === -1) {
                                    diffToTarget =
                                        scrollSnap - (1 + scrollProgress);
                                }
                                if (sign === 1) {
                                    diffToTarget =
                                        scrollSnap + (1 - scrollProgress);
                                }
                            }
                        });
                    }

                    const tweenValue =
                        1 - Math.abs(diffToTarget * tweenFactor.current);
                    const opacity = numberWithinRange(
                        tweenValue,
                        0,
                        1
                    ).toString();
                    emblaApi.slideNodes()[slideIndex].style.opacity = opacity;
                });
            });
        },
        []
    );

    useEffect(() => {
        if (!emblaApi) return;

        setTweenFactor(emblaApi);
        tweenOpacity(emblaApi);
        emblaApi
            .on("reInit", setTweenFactor)
            .on("reInit", tweenOpacity)
            .on("scroll", tweenOpacity)
            .on("slideFocus", tweenOpacity);
    }, [emblaApi, tweenOpacity]);

    return (
        <SCEmbla>
            <div className="embla">
                <div className="embla__viewport" ref={emblaRef}>
                    <div className="embla__container">
                        {slides.map((item, index) => (
                            <div className="embla__slide" key={index}>
                                <PlanAndPriceItemForId item={item} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className="text-container mobile-view">
                <div className="statusbar-container">
                    {scrollSnaps.map((_, index) => (
                        <div
                            className={"statusbar".concat(
                                index === selectedIndex ? " selected" : ""
                            )}
                            key={index}
                        >
                            &nbsp;
                        </div>
                    ))}
                </div>
            </div>
        </SCEmbla>
    );
};

export default EmblaCarousel;

const SCEmbla = styled.div`
    .embla {
        // max-width: 70rem;
        margin: auto;
        --slide-height: 19rem;
        --slide-spacing: 1rem;
        --slide-size: 100%;
        --slide-spacing-sm: 1.6rem;
        // --slide-size-sm: 50%;
        --slide-size-sm: 100%;
        --slide-spacing-lg: 2rem;
        // --slide-size-lg: calc(100% / 3);
        --slide-size-lg: 100%;

        --text-body: rgb(54, 49, 61);
        --text-high-contrast-rgb-value: 49, 49, 49;
        --detail-medium-contrast: rgb(234, 234, 234);

        display: flex;
    }
    .embla__viewport {
        overflow: hidden;
        flex: 1;
    }
    .embla__container {
        backface-visibility: hidden;
        display: flex;
        touch-action: pan-y pinch-zoom;
        margin-left: calc(var(--slide-spacing) * -1);
    }
    .item {
        background: ${theme["color-white"]};
        padding: ${theme["spacing-l"]};
        width: 100%;
        // height: 280px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        &:last-item {
            margin-right: 0;
        }

        &.basic {
            .plan {
                color: ${theme["color-blue"]};
                margin-bottom: ${theme["spacing-sm"]};
            }
        }

        &.premium {
            border: 1px solid ${theme["color-price-yellow"]};
            padding-top: 0;
            justify-content: normal;
            align-items: flex-end;

            .premium {
                flex: 1;
                width: 100%;
                display: flex;
                flex-direction: column;
                justify-content: space-between;
            }

            .plan {
                color: ${theme["color-price-yellow"]};
                margin-bottom: ${theme["spacing-sm"]};
            }

            .popular {
                background-color: ${theme["color-price-yellow"]};
                color: ${theme["color-white"]};
                width: fit-content;
                padding: ${theme["spacing-s"]};
                float: right;
                height: 32px;
                border-radius: 5px;
            }
        }

        &.unlimited {
            .plan {
                color: ${theme["color-price-purple"]};
                margin-bottom: ${theme["spacing-sm"]};
            }
        }

        .price {
            .text-largexlarge {
                color: ${theme["color-black"]};
            }
        }

        .text-medium > div {
            margin-bottom: ${theme["spacing-sm"]};

            &:last-child {
                margin-bottom: 0;
            }
        }
    }
    .text-container {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        &.mobile-view {
            ${breakpoints(
                "display",
                "",
                [{ [(parseInt(size.px.xs) + 1).toString()]: "none" }],
                "min-width"
            )};
            margin-top: ${theme["spacing-m"]};
            align-items: normal;

            .statusbar-container {
                display: flex;
                width: 100%;
                height: 2px;
                margin-bottom: ${theme["spacing-m"]};

                .statusbar {
                    background-color: ${theme["color-light-grey"]};
                    flex: 1;
                    margin-right: ${theme["spacing-ml"]};

                    &.selected {
                        background-color: ${theme["color-blue"]};
                    }

                    &:last-child {
                        margin-right: 0;
                    }
                }
            }
        }
    }
    @media (min-width: 750px) {
        .embla__container {
            margin-left: calc(var(--slide-spacing-sm) * -1);
        }
    }
    @media (min-width: 1200px) {
        .embla__container {
            margin-left: calc(var(--slide-spacing-lg) * -1);
        }
    }
    .embla__slide {
        min-width: 0;
        flex: 0 0 var(--slide-size);
        padding-left: var(--slide-spacing);
        display: flex;
        justify-content: center;
    }
    @media (min-width: 750px) {
        .embla__slide {
            flex: 0 0 var(--slide-size-sm);
            padding-left: var(--slide-spacing-sm);
        }
    }
    @media (min-width: 1200px) {
        .embla__slide {
            flex: 0 0 var(--slide-size-lg);
            padding-left: var(--slide-spacing-lg);
        }
    }
    .embla__slide__number {
        box-shadow: inset 0 0 0 0.2rem var(--detail-medium-contrast);
        border-radius: 1.8rem;
        font-size: 4rem;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        height: var(--slide-height);
        user-select: none;
    }
    .embla__controls {
        display: grid;
        grid-template-columns: auto 1fr;
        justify-content: space-between;
        gap: 1.2rem;
        margin-top: 1.8rem;
    }
    .embla__buttons {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.6rem;
        align-items: center;
    }
    .embla__button {
        -webkit-tap-highlight-color: rgba(
            var(--text-high-contrast-rgb-value),
            0.5
        );
        -webkit-appearance: none;
        appearance: none;
        background-color: transparent;
        touch-action: manipulation;
        display: inline-flex;
        text-decoration: none;
        cursor: pointer;
        border: 0;
        padding: 0;
        margin: 0;
        box-shadow: inset 0 0 0 0.2rem var(--detail-medium-contrast);
        width: 3.6rem;
        height: 3.6rem;
        z-index: 1;
        border-radius: 50%;
        color: var(--text-body);
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .embla__button:disabled {
        color: var(--detail-high-contrast);
    }
    .embla__button__svg {
        width: 35%;
        height: 35%;
    }
    .embla__dots {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        align-items: center;
        margin-right: calc((2.6rem - 1.4rem) / 2 * -1);
    }
    .embla__dot {
        -webkit-tap-highlight-color: rgba(
            var(--text-high-contrast-rgb-value),
            0.5
        );
        -webkit-appearance: none;
        appearance: none;
        background-color: transparent;
        touch-action: manipulation;
        display: inline-flex;
        text-decoration: none;
        cursor: pointer;
        border: 0;
        padding: 0;
        margin: 0;
        width: 2.6rem;
        height: 2.6rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
    }
    .embla__dot:after {
        box-shadow: inset 0 0 0 0.2rem var(--detail-medium-contrast);
        width: 1.4rem;
        height: 1.4rem;
        border-radius: 50%;
        display: flex;
        align-items: center;
        content: "";
    }
    .embla__dot--selected:after {
        box-shadow: inset 0 0 0 0.2rem var(--text-body);
    }
`;
